import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { M365UsersTab } from '../components/m365users'

interface License {
  id: string
  productName: string
  skuPartNumber: string
  totalUnits: number
  activeUnits: number
  consumedUnits: number
  availableUnits: number
  suspendedUnits: number
  warningUnits: number
  capabilityStatus: string
}

interface Subscription {
  id: string
  offerName: string
  friendlyName: string
  quantity: number
  status: string
  creationDate: string
  effectiveStartDate: string
  commitmentEndDate: string
  autoRenewEnabled: boolean
  isTrial: boolean
  billingCycle: string
  termDuration: string
}

interface AzureSubscription {
  id: string
  subscriptionName: string
  offerName?: string
  status: string
  billingCycle?: string
  creationDate?: string
  commitmentEndDate?: string
  autoRenewEnabled?: boolean
}

interface CustomerMapping {
  integrationId: string
  externalId: string
  externalName?: string
  syncStatus?: string
  lastSyncAt?: string
}

interface Customer {
  id: string
  name: string
  domain?: string
  status: string
  primaryContact?: string
  primaryEmail?: string
  phone?: string
  address?: string
  notes?: string
  totalLicenses: number
  totalUsers: number
  hasAzureSubscription: boolean
  lastActivityAt?: string
  mappings?: CustomerMapping[]
}

export default function CustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const [customer, setCustomer] = useState<Customer | null>(null)
  const [licenses, setLicenses] = useState<License[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [azureSubscriptions, setAzureSubscriptions] = useState<AzureSubscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'users'>('overview')

  useEffect(() => {
    async function fetchCustomerDetails() {
      if (!customerId) return

      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()

        // Fetch customer data using the Setup API (Customer 360)
        const customerData = await api.getSetupCustomer(token, customerId)

        if (!customerData.customer) {
          setError('Customer not found')
          return
        }
        setCustomer(customerData.customer)

        // Get Partner Center ID from mappings to fetch licenses
        const pcMapping = customerData.customer.mappings?.find(
          (m: CustomerMapping) => m.integrationId === 'partner-center'
        )

        if (pcMapping) {
          // Fetch licenses, subscriptions, and Azure subscriptions using Partner Center ID
          const licenseData = await api.getLicenses(token, pcMapping.externalId)
          setLicenses(licenseData.licenses || [])
          setSubscriptions(licenseData.subscriptions || [])
          setAzureSubscriptions(licenseData.azureSubscriptions || [])
        }
      } catch (err) {
        console.error('Failed to fetch customer details:', err)
        setError('Failed to load customer details. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchCustomerDetails()
  }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading customer details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="px-4 sm:px-0">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || 'Customer not found'}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/customers')}
                  className="text-sm font-medium text-red-800 hover:text-red-700"
                >
                  ← Back to Customers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/customers')}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 mb-4"
        >
          ← Back to Customers
        </button>
        <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>
        <p className="mt-1 text-sm text-gray-500">Customer details and license information</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="h-5 w-5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              M365 Users
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' ? (
        <M365UsersTab
          customerId={customerId!}
          partnerCenterId={customer.mappings?.find(m => m.integrationId === 'partner-center')?.externalId}
        />
      ) : (
        <>
      {/* Two Column Layout: Customer Information and Azure Subscriptions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Customer Information Card */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Customer Information</h3>
              <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                customer.status === 'Active'
                  ? 'bg-green-100 text-green-800'
                  : customer.status === 'Inactive'
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {customer.status}
              </span>
            </div>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Domain</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.domain || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Primary Email</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.primaryEmail || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.primaryContact || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 text-sm text-gray-900">{customer.phone || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Licenses</dt>
                <dd className="mt-1 text-sm text-gray-900 font-semibold">{customer.totalLicenses.toLocaleString()}</dd>
              </div>
              {customer.address && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.address}</dd>
                </div>
              )}
              {customer.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.notes}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Azure Subscriptions Card */}
        <div className="bg-white shadow sm:rounded-lg border-l-4 border-sky-400">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Azure Subscriptions</h3>
              {customer.hasAzureSubscription && (
                <span className="inline-flex rounded-full bg-sky-100 px-2 text-xs font-semibold leading-5 text-sky-800">
                  {azureSubscriptions.length} subscription{azureSubscriptions.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {!customer.hasAzureSubscription ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No Azure subscriptions</p>
              </div>
            ) : azureSubscriptions.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-sky-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">Azure Plan detected, subscription details loading...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {azureSubscriptions.map((azureSub) => (
                  <div key={azureSub.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{azureSub.subscriptionName}</span>
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        azureSub.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : azureSub.status === 'suspended'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {azureSub.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {azureSub.offerName && <span>{azureSub.offerName}</span>}
                      {azureSub.billingCycle && <span className="ml-2">• {azureSub.billingCycle}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {customer.hasAzureSubscription && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to={`/customers/${customerId}/azure-billing`}
                  className="inline-flex items-center text-sm font-medium text-sky-600 hover:text-sky-800"
                >
                  View Azure Billing Details
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscriptions Section */}
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            M365 Subscriptions ({subscriptions.length})
          </h3>
          {subscriptions.length === 0 ? (
            <p className="text-sm text-gray-500">No subscriptions found for this customer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offer Name
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Friendly Name
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Billing
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      End Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td className="px-3 py-4 text-sm text-gray-900">{subscription.offerName}</td>
                      <td className="px-3 py-4 text-sm text-gray-500">{subscription.friendlyName}</td>
                      <td className="px-3 py-4 text-sm text-gray-900">{subscription.quantity}</td>
                      <td className="px-3 py-4 text-sm">
                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          subscription.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === 'suspended'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {subscription.status}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">{subscription.billingCycle}</td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        {subscription.commitmentEndDate ? new Date(subscription.commitmentEndDate).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Licenses Section */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Licenses ({licenses.length})
          </h3>
          {licenses.length === 0 ? (
            <p className="text-sm text-gray-500">No licenses found for this customer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consumed
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Available
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {licenses.map((license) => (
                    <tr key={license.id}>
                      <td className="px-3 py-4 text-sm text-gray-900">{license.productName}</td>
                      <td className="px-3 py-4 text-sm text-gray-500 font-mono text-xs">{license.skuPartNumber}</td>
                      <td className="px-3 py-4 text-sm text-gray-900">{license.totalUnits}</td>
                      <td className="px-3 py-4 text-sm text-gray-900">{license.consumedUnits}</td>
                      <td className="px-3 py-4 text-sm text-gray-900">{license.availableUnits}</td>
                      <td className="px-3 py-4 text-sm">
                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                          license.capabilityStatus === 'Enabled'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {license.capabilityStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  )
}
